import { Fragment, useCallback, useMemo } from "react"
import { Box, useTheme } from "@mui/material"

import { getIcon } from "utils/icon"
import { SKELETON_COUNT, SKELETON_INDEX_TO_REF } from "constant"

import { useTranslation } from "providers/i18n"
import { useDining } from "extensions/iconsiam/hooks/dining"

import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"
import { Head } from "components/Head"

import { ImageSkeletonCard } from "components/ImageCard"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import { DiningIcon } from "components/icons"
import { EmptyData } from "components/EmptyData"
import { PageLayout } from "extensions/iconsiam/layouts/PageLayout"
import { Header } from "extensions/iconsiam/components/Header"
import { VenueFilterBar } from "extensions/iconsiam/components/VenueFilterBar"
import { DiningCard } from "extensions/iconsiam/components/domains/dining/DiningCard"
import { HeaderSearchButton } from "extensions/iconsiam/components/Header/HeaderSearchButton"

export const DiningPage = ({ Layout = PageLayout }) => {
  const {
    restaurants,
    currentCategory,
    categories,
    venueFilter,
    hasData,
    hasNextPage,
    endScrollRef,
    filterByCategoryId,
    filterByVenueId,
  } = useDining()

  const { t } = useTranslation({ defaultValue: null })

  const handleChangeCategory = useCallback(
    (e, id) => {
      filterByCategoryId(id)
    },
    [filterByCategoryId]
  )

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.dining) || <DiningIcon />,
    [theme]
  )

  const { styleOverrides = {} } = theme.components?.[`AppDining`] || {}
  const {
    root,
    filterSection,
    filterContainer,
    diningImageCard,
    filterButton,
    gridContainer,
    emptyDataContainer,
  } = styleOverrides

  return (
    <>
      <Head title={t("Dining")} />
      <Layout
        header={
          <Header
            filterBar={
              <VenueFilterBar
                onClickVenue={filterByVenueId}
                value={venueFilter}
              />
            }
            searchButton={<HeaderSearchButton />}
          />
        }
      >
        <Box sx={root}>
          {(hasData || categories) && (
            <Box sx={filterSection}>
              <HorizontalScrollPane
                className="subCategory-filterbar"
                stackProps={{
                  sx: filterContainer,
                }}
              >
                <CategoryFilterBar
                  sx={filterButton}
                  activeCategory={currentCategory}
                  categories={categories}
                  onChange={handleChangeCategory}
                  renderFilterButton={CategoryFilterChip}
                />
              </HorizontalScrollPane>
            </Box>
          )}
          {hasData ? (
            <>
              <Box sx={gridContainer}>
                {restaurants.pages.map((page, pageNum) => {
                  return (
                    <Fragment key={pageNum}>
                      {page.data.map((restaurant, index) => {
                        const { isNewShop = false, isComingSoon = false } =
                          restaurant.properties
                        return (
                          <DiningCard
                            id={restaurant?.id}
                            data-testid={restaurant?.id}
                            data={restaurant}
                            sx={diningImageCard}
                            isNewShop={isNewShop}
                            isComingSoon={isComingSoon}
                            key={`dining-${index}`}
                          />
                        )
                      })}
                    </Fragment>
                  )
                })}
                {hasNextPage &&
                  Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                    <ImageSkeletonCard
                      ref={
                        index === SKELETON_INDEX_TO_REF ? endScrollRef : null
                      }
                      key={index}
                    />
                  ))}
              </Box>
            </>
          ) : (
            <Box sx={emptyDataContainer}>
              <EmptyData
                icon={emptyIcon}
                primary={t("Coming soon")}
                secondary={t("Stay tuned for the update")}
              />
            </Box>
          )}
        </Box>
      </Layout>
    </>
  )
}
